/**
 * Desafío TOTP -- el usuario ya está enrolado, solo prueba el código del
 * momento. Llega aquí via RequireSession -> guards.tsx, que redirige
 * cuando `mfa === 'needs_challenge'`.
 *
 * Los rechazos se explican (lib/mfa.ts): sesión de verificación vencida, código vencido, mal formado o equivocado.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { mfaChallenge } from '../lib/backend';
import { CLEARS_CODE, CODE_LENGTH, classifyMfaError, type MfaFailure } from '../lib/mfa';
import { CodeField, SessionExpired } from '../components/MfaShared';
import { Wordmark } from '../components/Logo';

export function MfaChallenge() {
  const { pendingMfaToken, completeMfa } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [code, setCode] = useState('');
  const [failure, setFailure] = useState<MfaFailure | null>(null);
  const [busy, setBusy] = useState(false);

  if (!pendingMfaToken || failure?.kind === 'session_expired') return <SessionExpired message={failure?.message} />;

  async function verify() {
    if (!pendingMfaToken) return;
    setBusy(true);
    setFailure(null);
    try {
      const token = await mfaChallenge(pendingMfaToken, code.trim());
      await completeMfa(token);
      const from = (loc.state as { from?: string } | null)?.from ?? '/';
      nav(from, { replace: true });
    } catch (e) {
      // Se dice POR QUE fallo (venció, mal formado, equivocado, sin conexión...) en vez de un "incorrecto" genérico.
      const f = classifyMfaError(e);
      setFailure(f);
      if (CLEARS_CODE.has(f.kind)) {
        setCode('');
        document.getElementById('mfa-code')?.focus();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authwrap">
      <form
        className="authcard"
        style={{ maxWidth: 380 }}
        onSubmit={(e) => {
          e.preventDefault();
          void verify();
        }}
      >
        <div className="authbrand">
          <Wordmark size={38} />
        </div>
        <h2 style={{ fontSize: 19, marginBottom: 4 }}>Verificación en dos pasos</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 18 }}>
          Ingrese el código de 6 dígitos de su aplicación de autenticación.
        </p>

        <CodeField
          id="mfa-code"
          label="Código"
          value={code}
          onChange={(c) => {
            setCode(c);
            setFailure(null);
          }}
          error={failure?.message}
          autoFocus
        />

        <button className="btn btn--primary" style={{ width: '100%', justifyContent: 'center' }} disabled={busy || code.length !== CODE_LENGTH}>
          {busy ? 'Verificando…' : 'Verificar'}
        </button>

        <p className="muted" style={{ fontSize: 12, marginTop: 16, marginBottom: 0 }}>
          Si tiene problemas con el autenticador (cambio de teléfono u otros) contactarse con el encargado.
        </p>
      </form>
    </div>
  );
}
